import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { highlightMatch } from 'app/components/People/PeopleSidebar/PeopleSideBarSearch';
import {
  PeopleSidebarSearchContentItemCompany,
  PeopleSidebarSearchContentItemContainer,
  PeopleSidebarSearchContentItemName,
  PeopleSidebarSearchContentItemRightContainer,
  PeopleSidebarSearchContentList,
  PeopleSidebarSearchContentLoadingContainer,
  PeopleSidebarSearchNoMatches,
} from 'app/components/People/PeopleSidebar/PeopleSideBarSearch/styled';
import usePeopleSearch from 'app/components/People/hooks/usePeopleSearch';
import {
  CloseIcon,
  PersonIcon,
  SearchIconSmall,
  SortingIcon,
  Spinner,
} from 'app/components/shared/icons';
import { useFormikContext } from 'formik';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import useDebounce from 'hooks/useDebounce';
import { ThemeProvider } from 'styled-components';

import {
  PersonSelectIconContainer,
  PersonSelectInput,
  PersonSelectOptionsContainer,
  PersonSelectSearchIconContainer,
  PersonSelectSelectedPeopleContainer,
  PersonSelectSelectedPersonContainer,
  PersonSelectSelectedPersonLabel,
  PersonSelectSelectedPersonRemoveContainer,
  PersonSelectWrapper,
} from './styled';
import usePersonSelect from './usePersonSelect';

interface PersonSelectProps {
  /**
   * Array of people slugs
   */
  initialValues?: string[];

  name?: string;

  placeholder?: string;

  onChange?: (values: any[]) => void;

  allowMultiple?: boolean;

  preselectedValues?: any[];
}

export default function PersonSelect({
  placeholder = 'No person selected',
  name,
  onChange,
  allowMultiple = false,
  preselectedValues = [],
}: PersonSelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const formikContext = useFormikContext();
  const [selectedPeople, setSelectedPeople] = useState<any[]>(preselectedValues);
  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue = useDebounce(searchValue, 250);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && searchValue.trim() === '' && selectedPeople.length > 0) {
      const lastPerson = selectedPeople[selectedPeople.length - 1];
      handlePersonClick(lastPerson);
    }
  };

  const searchQuery = usePersonSelect(searchDebouncedValue, {
    enabled: false,
  });
  const { data, isLoading } = searchQuery;

  useEffect(() => {
    if (searchDebouncedValue && searchDebouncedValue.trim().length > 0) {
      searchQuery.refetch();
    }
  }, [searchDebouncedValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const showContent = useMemo(() => {
    return searchDebouncedValue.trim().length > 0;
  }, [searchDebouncedValue]);

  const handlePersonClick = (person) => {
    let finalPeople = [];

    if (allowMultiple) {
      const personIndex = selectedPeople.findIndex(
        (selectedCompany) => selectedCompany.uuid === person.uuid
      );

      finalPeople =
        personIndex === -1
          ? [...selectedPeople, person]
          : selectedPeople.filter((selectedCompany) => selectedCompany.uuid !== person.uuid);
    } else {
      finalPeople = [person];
    }

    setSelectedPeople(finalPeople);

    if (name) {
      formikContext.setFieldValue(
        name,
        finalPeople.map((person) => person.uuid)
      );
    }

    onChange?.(finalPeople);
    setSearchValue('');
  };

  const handlePersonRemoveClick = (person, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    const finalPeople = selectedPeople.filter(
      (selectedPerson) => selectedPerson.uuid !== person.uuid
    );

    setSelectedPeople(finalPeople);

    if (name) {
      formikContext.setFieldValue(
        name,
        finalPeople.map((person) => person.uuid)
      );
    }

    onChange?.(finalPeople);
  };

  const isError = useMemo<boolean>(() => {
    return formikContext.errors[name] && formikContext.touched[name];
  }, [formikContext.errors[name], formikContext.touched[name], name]);

  useOnClickOutside(wrapperRef, () => {
    if (searchValue) {
      formikContext.setFieldTouched(name, true, false);
    }
    setSearchValue('');
  });

  const isPersonSelected = useCallback(
    (person) => {
      return selectedPeople.find((selectedPerson) => selectedPerson.uuid === person.uuid);
    },
    [selectedPeople]
  );

  const handleSelectedPeopleContainerClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    inputRef.current.focus();
  };

  return (
    <ThemeProvider theme={{ isError, hasPeople: selectedPeople.length > 0 }}>
      <PersonSelectWrapper ref={wrapperRef} onClick={handleSelectedPeopleContainerClick}>
        <input type="hidden" name={name} value={formikContext.values[name]} />
        <PersonSelectSelectedPeopleContainer>
          {selectedPeople.length > 0 &&
            selectedPeople.map((person) => (
              <PersonSelectSelectedPersonContainer key={person.slug}>
                <PersonIcon width={16} height={16} fill={'var(--color-data-people-foreground)'} />
                <PersonSelectSelectedPersonLabel>{person.name}</PersonSelectSelectedPersonLabel>
                <PersonSelectSelectedPersonRemoveContainer
                  data-action="remove"
                  onClick={(e) => handlePersonRemoveClick(person, e)}
                >
                  <CloseIcon width={9} height={9} fill="var(--color-text)" fillOpacity="1" />
                </PersonSelectSelectedPersonRemoveContainer>
              </PersonSelectSelectedPersonContainer>
            ))}
          <PersonSelectInput
            ref={inputRef}
            name={name}
            placeholder={selectedPeople.length > 0 ? null : placeholder}
            onChange={handleInputChange}
            value={searchValue}
            onBlur={() => formikContext.setFieldTouched(name, true, false)}
            onKeyDown={handleKeyDown}
          />
        </PersonSelectSelectedPeopleContainer>
        <PersonSelectIconContainer>
          <SearchIconSmall width={24} height={24} fill="var(--color-primary)" />
        </PersonSelectIconContainer>
        <AnimatePresence>
          {showContent ? (
            <PersonSelectOptionsContainer>
              {!isLoading ? (
                <PeopleSidebarSearchContentList>
                  {data && data?.length > 0 ? (
                    data?.map((person) => (
                      <PeopleSidebarSearchContentItemContainer
                        key={person.uuid}
                        onClick={() => handlePersonClick(person)}
                      >
                        <PeopleSidebarSearchContentItemName
                          dangerouslySetInnerHTML={{
                            __html: highlightMatch(searchDebouncedValue, person.name).text,
                          }}
                        />
                        <PeopleSidebarSearchContentItemRightContainer>
                          <PeopleSidebarSearchContentItemCompany>
                            {person.info.replace('Person from ', '')}
                          </PeopleSidebarSearchContentItemCompany>
                          {isPersonSelected(person) ? (
                            <PeopleSidebarSearchContentItemCompany>
                              ALREADY ADDED
                            </PeopleSidebarSearchContentItemCompany>
                          ) : null}
                        </PeopleSidebarSearchContentItemRightContainer>
                      </PeopleSidebarSearchContentItemContainer>
                    ))
                  ) : (
                    <PeopleSidebarSearchNoMatches>No matches</PeopleSidebarSearchNoMatches>
                  )}
                </PeopleSidebarSearchContentList>
              ) : (
                <PeopleSidebarSearchContentLoadingContainer>
                  <Spinner width={24} height={24} fill="var(--color-primary)" />
                </PeopleSidebarSearchContentLoadingContainer>
              )}
            </PersonSelectOptionsContainer>
          ) : null}
        </AnimatePresence>
      </PersonSelectWrapper>
    </ThemeProvider>
  );
}
